var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import * as React from 'react';
import { Button, Grid, Link, TextField, PasswordField } from '@emburse/ui-core';
import { EmburseAccountsLeftContentContainer } from '@ReactComponents/Shared/Account/EmburseAccountsLeftContentContainer';
import { TestSiteBanner } from '@ReactComponents/Shared/TestSiteBanner/TestSiteBanner';
import { SamlEmailVerificationApi } from '@ClientServices/api/samlEmailVerificationApi';
import { AccountIconWithEmail } from './AccountIconWithEmail';
import { getUserEmailFromSessionStorage, removeUserEmailFromSessionStorage, setUserEmailToSessionStorage } from './LoginFormSessionStorage';
import { PrivacyPolicy } from './PrivacyPolicy';
export const RedesignedLoginForm = ({ csrf, onSubmit }) => {
    var _a;
    const [emailFieldEnabled, setEmailFieldEnabled] = React.useState(true);
    const [email, setEmail] = React.useState('');
    const [emailVerificationLoading, setEmailVerificationLoading] = React.useState(false);
    const [showPasswordField, setShowPasswordField] = React.useState(false);
    // Rendering the TestSiteBanner pushes the privacy policy down too far
    const privacyPolicyGridHeight = window.FEATURES.test_site ? '600px' : '700px';
    React.useEffect(() => {
        const userEmailFromSessionStorage = getUserEmailFromSessionStorage();
        if (userEmailFromSessionStorage) {
            setEmailFieldEnabled(false);
            setEmail(userEmailFromSessionStorage);
            setShowPasswordField(true);
        }
    }, []);
    const handleEmailChange = (e) => __awaiter(void 0, void 0, void 0, function* () {
        e.preventDefault();
        if (email) {
            setUserEmailToSessionStorage(email);
            setEmailFieldEnabled(false);
            setEmailVerificationLoading(true);
            try {
                const res = yield SamlEmailVerificationApi.checkIfUserHasSSO(email, csrf);
                if (res.redirectUrl) {
                    window.location.href = res.redirectUrl;
                    return;
                }
            }
            catch (error) {
                /* Do nothing */
            }
            setEmailVerificationLoading(false);
            setShowPasswordField(true);
        }
    });
    const onFormSubmit = (e) => {
        // If the user presses enter after typing in their email, the form will submit
        // But we instead want this to just "submit" the email, i.e. lock it in and then show the password field
        if (emailFieldEnabled && !showPasswordField) {
            e.preventDefault();
            handleEmailChange(e);
        }
        else {
            removeUserEmailFromSessionStorage();
            onSubmit(e);
        }
    };
    const renderEmailField = () => {
        if (emailFieldEnabled) {
            return (_jsx(TextField, { id: "emailField", type: "email", name: "email", label: "Enter email", autoFocus: true, fullWidth: true, disabled: !emailFieldEnabled, value: email, onChange: (e) => setEmail(e.target.value), required: true, "data-testid": "emailField" }));
        }
        return (_jsxs(_Fragment, { children: [_jsx("input", { type: "hidden", name: "email", value: email }), _jsxs(Grid, Object.assign({ container: true, direction: 'row', justifyContent: 'space-between' }, { children: [_jsx(Grid, Object.assign({ item: true, direction: 'row', alignItems: 'center' }, { children: _jsx(AccountIconWithEmail, { className: "jc-fe", email: email }) })), _jsx(Grid, Object.assign({ item: true }, { children: _jsx(Button, Object.assign({ size: "small", variant: "text", onClick: () => {
                                    setEmailFieldEnabled(true);
                                    setShowPasswordField(false);
                                }, "data-testid": "editEmailButton" }, { children: "Edit" })) }))] }))] }));
    };
    const renderLogInButton = () => {
        if (emailFieldEnabled) {
            return (_jsx(Button, Object.assign({ size: "medium", fullWidth: true, onClick: handleEmailChange, "data-testid": "continueButton" }, { children: "Continue" })));
        }
        return (_jsx(Button, Object.assign({ id: "loginButton", className: "login", type: "submit", size: "medium", fullWidth: true, isLoading: emailVerificationLoading }, { children: "Log In" })));
    };
    function renderDowntime() {
        var _a, _b;
        return _jsxs("div", { children: [(_b = (_a = window === null || window === void 0 ? void 0 : window.whitelabelConfiguration) === null || _a === void 0 ? void 0 : _a.tags) === null || _b === void 0 ? void 0 : _b.brandName, " is currently unavailable due to scheduled maintenance."] });
    }
    function renderLoginForm() {
        var _a;
        if ((_a = window === null || window === void 0 ? void 0 : window.FEATURES) === null || _a === void 0 ? void 0 : _a.planned_database_downtime) {
            return renderDowntime();
        }
        return (_jsxs("form", Object.assign({ method: "POST", action: "/login", id: "login", autoComplete: "off", onSubmit: onFormSubmit }, { children: [_jsx("input", { type: "hidden", name: "_csrf", value: csrf }), _jsxs(Grid, Object.assign({ container: true, direction: 'column', spacing: 2, style: { width: '400px' } }, { children: [_jsx(Grid, Object.assign({ item: true }, { children: renderEmailField() })), showPasswordField && (_jsx(Grid, Object.assign({ item: true }, { children: _jsx(PasswordField, { id: "passwordField", name: "password", label: "Enter Password", autoComplete: "off", fullWidth: true, autoFocus: true, required: true }) }))), _jsx(Grid, Object.assign({ item: true }, { children: renderLogInButton() })), showPasswordField && (_jsx(Grid, Object.assign({ item: true }, { children: _jsx("div", Object.assign({ className: "forgotContainer" }, { children: _jsx(Link, Object.assign({ href: "/reset_password" }, { children: "Forgot Password?" })) })) })))] }))] })));
    }
    const title = ((_a = window === null || window === void 0 ? void 0 : window.FEATURES) === null || _a === void 0 ? void 0 : _a.planned_database_downtime) ? '' : 'Sign In';
    return (_jsxs(EmburseAccountsLeftContentContainer, Object.assign({ title: title }, { children: [_jsx(Grid, Object.assign({ item: true }, { children: _jsx(TestSiteBanner, {}) })), _jsx(Grid, Object.assign({ item: true }, { children: renderLoginForm() })), _jsx(Grid, Object.assign({ item: true, style: { height: privacyPolicyGridHeight, alignContent: 'flex-end', width: '450px' } }, { children: _jsx(PrivacyPolicy, {}) }))] })));
};
